import React from 'react';
import { Form } from '@ant-design/compatible';
import { FormComponentProps } from '@ant-design/compatible/lib/form';

import CategoryForm, { Content, Label, Row } from '../../common/CategoryForm';
import RadioGroupForm from '../../../../components/form/RadioGroupForm';
import InputForm from '../../../../components/form/InputForm';
import ImgUploadForm from '../../../../components/form/ImgUploadForm';
import Button from '../../../../components/Button';
import { formRequiredRules } from '../../../../lib/formRules';
import { REX_CATEGORY_NAME } from '../../../../lib/rex';
import useCategoryCreateModal from '../../../../hooks/category/useCategoryCreateModal';
import {
  INVALID_CATEGORY_NAME_MESSAGE,
  INVALID_REQUIRED_CATEGORY_NAME_MESSAGE,
  INVALID_REQUIRED_MESSAGE,
} from '../../../../constants/service/invalidMsg/categoryInvalidMsg';
import {
  CATEGORY_BANNER_IMG_HEIGHT,
  CATEGORY_BANNER_IMG_WIDTH,
  CATEGORY_EXPOSE_LOCATION_TYPE_SELECT_LIST,
  CATEGORY_MEMO_TEXT_MAX_LENGTH,
  CATEGORY_SORT_TYPE_SELECT_LIST,
  CATEGORY_STATUS_TYPE,
  CATEGORY_STATUS_TYPE_SELECT_LIST,
  CATEGORY_TYPE,
  CATEGORY_TYPE_SELECT_LIST,
  EMPHASIS_CATEGORY_NAME_MAX_LENGTH,
  GENERAL_CATEGORY_NAME_MAX_LENGTH,
} from '../../../../constants/service/app/categoryConstants';
import { REQUIRED_MSG } from '../../../../constants/service/common/formRulesConstants';
import { IMAGE_SIZE_GUIDE_TEXT, IMAGE_TYPE_GUIDE_TEXT } from '../../../../constants/service/common/commonConstants';
import './CategoryCreateWrap.scss';

const CategoryCreateWrap = ({ form }: FormComponentProps): JSX.Element => {
  const { onClickSaveConfirmBtn, onClickCancelConfirmBtn } = useCategoryCreateModal(form);
  const isEmphasisCategory = form.getFieldValue('type') === CATEGORY_TYPE.EMPHASIS;

  return (
    <div className="category-create-wrap">
      <div className="main-title">1차 카테고리 생성</div>
      <span className="option-title">필수 입력</span>
      <CategoryForm>
        <Row>
          <Label text="종류" required />
          <Content>
            <RadioGroupForm
              className="category-type"
              form={form}
              name="type"
              rules={[formRequiredRules(true, INVALID_REQUIRED_MESSAGE, false)]}
              list={CATEGORY_TYPE_SELECT_LIST}
              initialValue={CATEGORY_TYPE.GENERAL}
            />
          </Content>
          <Label text="카테고리 활성여부" required />
          <Content>
            <RadioGroupForm
              className="category-status"
              form={form}
              name="status"
              rules={[formRequiredRules(true, INVALID_REQUIRED_MESSAGE, false)]}
              list={CATEGORY_STATUS_TYPE_SELECT_LIST}
              initialValue={CATEGORY_STATUS_TYPE.ACTIVE}
            />
          </Content>
        </Row>
        <Row>
          <Label
            text="카테고리 명"
            required
            withCount
            content={form.getFieldValue('name')}
            maxLength={isEmphasisCategory ? EMPHASIS_CATEGORY_NAME_MAX_LENGTH : GENERAL_CATEGORY_NAME_MAX_LENGTH}
          />
          <Content>
            <InputForm
              className="category-name"
              form={form}
              name="name"
              initialValue={null}
              rules={[
                formRequiredRules(true, INVALID_REQUIRED_CATEGORY_NAME_MESSAGE),
                { pattern: REX_CATEGORY_NAME, message: INVALID_CATEGORY_NAME_MESSAGE },
              ]}
              maxLength={isEmphasisCategory ? EMPHASIS_CATEGORY_NAME_MAX_LENGTH : GENERAL_CATEGORY_NAME_MAX_LENGTH}
              isEditable
              subContent={
                isEmphasisCategory ? (
                  <div>
                    *텍스트 6자+띄어쓰기 2칸 이상 입력할 경우 사용자 화면에 명칭이 잘려서 노출될 수 있습니다.
                    <br />
                    *카테고리 전시 진행 시 ‘미리 보기'를 통해 꼭 카테고리명 잘림 여부를 확인해 주세요
                  </div>
                ) : null
              }
            />
          </Content>
        </Row>
        <Row>
          <Label text="카테고리 생성 사유" />
          <Content>
            <InputForm
              className="category-create-memo"
              form={form}
              name="memo"
              initialValue={null}
              rules={[{ pattern: REX_CATEGORY_NAME, message: INVALID_CATEGORY_NAME_MESSAGE }]}
              maxLength={CATEGORY_MEMO_TEXT_MAX_LENGTH}
            />
          </Content>
        </Row>
        <Row>
          <Label
            text="노출 위치"
            required
            tooltip={
              <>
                <div className="grid-item">
                  • <strong>공통</strong>은 카테고리탭, 검색결과, 브랜드상세에서 노출됩니다.
                </div>
                <div className="grid-item">
                  • <strong>로컬</strong>은 카테고리탭에서만 노출됩니다.
                </div>
              </>
            }
          />
          <Content>
            <RadioGroupForm
              className="category-exposure-type"
              form={form}
              name="exposureType"
              rules={[formRequiredRules(true, INVALID_REQUIRED_MESSAGE, false)]}
              list={CATEGORY_EXPOSE_LOCATION_TYPE_SELECT_LIST}
            />
          </Content>
        </Row>
        <Row>
          <Label text="상품 기본정렬순 타입" required />
          <Content>
            <RadioGroupForm
              className="category-sort-type"
              form={form}
              name="sortType"
              rules={[formRequiredRules(true, INVALID_REQUIRED_MESSAGE, false)]}
              list={CATEGORY_SORT_TYPE_SELECT_LIST}
            />
          </Content>
        </Row>
        {isEmphasisCategory ? (
          <Row>
            <Label text="이미지" required />
            <Content>
              <ImgUploadForm
                form={form}
                isEditable={true}
                rules={[formRequiredRules(true, REQUIRED_MSG, true, 'array')]}
                minCnt={1}
                modalTitle="이미지"
                cropMode={false}
                capacityLimit={1}
                name="categoryAttachments"
                targetSize={{
                  width: CATEGORY_BANNER_IMG_WIDTH,
                  height: CATEGORY_BANNER_IMG_HEIGHT,
                }}
                restriction={{
                  square: true,
                  widthMin: CATEGORY_BANNER_IMG_WIDTH,
                  heightMin: CATEGORY_BANNER_IMG_WIDTH,
                }}
                subContent={
                  <div>
                    *비율 가로1: 세로1
                    <br />
                    *권장 크기: 216X216 px(권장 크기보다 작을 경우, 앱에서 화질 저하의 우려가 있습니다.)
                    <br />*{IMAGE_SIZE_GUIDE_TEXT}
                    <br />*{IMAGE_TYPE_GUIDE_TEXT}
                  </div>
                }
              />
            </Content>
          </Row>
        ) : null}
      </CategoryForm>
      <div className="button-list">
        <Button isRadius color="primary" text="저장" onClick={onClickSaveConfirmBtn} />
        <Button isRadius text="취소" onClick={onClickCancelConfirmBtn} />
      </div>
    </div>
  );
};

export default Form.create<FormComponentProps>()(CategoryCreateWrap);
